import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

/**
 * Общая логика прокрутки дат при восстановлении фильтра из localStorage.
 *
 * Правила:
 * - Если сохранённая конечная дата старше сегодняшней более чем на 1 день → обе даты становятся сегодняшними
 * - Если сохранённая конечная дата вчерашняя → начальная дата остаётся, конечная прокручивается на сегодня
 * - Если сохранённая конечная дата сегодняшняя → оставляем обе даты как есть
 */
export function applyDateRoll(savedDateEnd, defaultDateFrom, defaultDateTo, savedDateFrom) {
  const today = dayjs().endOf('day');
  if (savedDateEnd && today.diff(savedDateEnd, 'day') > 1) {
    return { dateFrom: today, dateTo: today };
  }
  if (savedDateEnd && savedDateEnd.isBefore(today, 'day')) {
    return {
      dateFrom: savedDateFrom || defaultDateFrom,
      dateTo: today,
    };
  }
  return {
    dateFrom: savedDateFrom || defaultDateFrom,
    dateTo: savedDateEnd || defaultDateTo,
  };
}

/**
 * Хук для сохранения фильтра журнала/отчёта в localStorage.
 *
 * @param {string} storageKey - ключ localStorage
 * @param {() => object} getDefaults - возвращает фильтр по умолчанию, если в localStorage нет сохранённого значения
 * @param {(parsed: object) => object} transformSaved - преобразует сырой объект из localStorage в состояние фильтра
 * @param {(filter: object) => object} transformToSave - преобразует состояние фильтра в сериализуемый объект для записи в localStorage
 * @returns {[object, function]} - [filter, setFilter], аналогично useState
 */
export function useLocalStorageFilter(storageKey, getDefaults, transformSaved, transformToSave) {
  const transformToSaveRef = useRef(transformToSave);
  transformToSaveRef.current = transformToSave;

  const [filter, setFilter] = useState(() => {
    const savedFilter = localStorage.getItem(storageKey);
    if (savedFilter) {
      try {
        const parsed = JSON.parse(savedFilter);
        return transformSaved(parsed);
      } catch (e) {
        console.warn(`Ошибка при загрузке фильтра ${storageKey} из localStorage:`, e);
      }
    }
    return getDefaults();
  });

  useEffect(() => {
    try {
      const toSave = transformToSaveRef.current(filter);
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
      console.warn(`Ошибка при сохранении фильтра ${storageKey} в localStorage:`, e);
    }
  }, [filter, storageKey]);

  return [filter, setFilter];
}
